import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";
import { waLink, DEFAULT_WA_MSG } from "@/lib/site";

const LINKS = [
  { label: "Home", href: "#top" },
  { label: "Occasions", href: "#collections" },
  { label: "Shop", href: "#products" },
  { label: "Corporate", href: "#corporate" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (href === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      scrollToId(href);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "bg-ivory/85 backdrop-blur-xl border-b border-gold/25 shadow-[0_8px_40px_-20px_rgba(139,30,34,0.25)]"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
          <a
            href="#top"
            onClick={(e) => go(e, "#top")}
            data-testid="nav-logo-link"
            aria-label="A to Z Gift and Craft House — home"
          >
            <Logo />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                className="text-[13px] font-semibold tracking-wide text-charcoal/80 hover:text-crimson transition-colors duration-300"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-crimson text-ivory text-[13px] font-semibold px-5 py-2.5 hover:bg-crimson-dark hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              <MessageCircle className="h-4 w-4" />
              Order on WhatsApp
            </a>
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-charcoal"
              aria-label="Open menu"
              data-testid="nav-menu-open-button"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] bg-noir/95 backdrop-blur-lg flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-4 sm:px-8 h-[72px]">
              <Logo light />
              <button
                onClick={() => setOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ivory/25 text-ivory"
                aria-label="Close menu"
                data-testid="nav-menu-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  className="font-display text-4xl sm:text-5xl text-ivory hover:text-gold-light transition-colors duration-300 py-2"
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href={waLink(DEFAULT_WA_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                className="mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-crimson text-ivory px-8 py-4 font-semibold"
                data-testid="mobile-whatsapp-button"
              >
                <MessageCircle className="h-5 w-5" />
                Order on WhatsApp
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
